import React from 'react';
import moonIcon from '../../assets/dark-mode/logo-short.png';
import StreamingText from '../StreamingText';
import RetrievalResults from '../RetrievalResults';
import type { ChatMessage } from '../../types';
import { isStructuredComponent, isMultiComponentResponse } from '../../types/structuredComponents';
import { ComponentRegistry, getComponentProps } from '../structured';
import './Message.scss';

interface MessageProps {
  message: ChatMessage;
  isLatest?: boolean;
  shouldStream?: boolean;
  onStreamComplete?: () => void;
  onStreamUpdate?: () => void;
  onSendMessage?: (content: string) => void;
}

const Message: React.FC<MessageProps> = ({
  message,
  isLatest = false,
  shouldStream = false,
  onStreamComplete,
  onStreamUpdate,
  onSendMessage
}) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Check if content is a structured component
  if (isStructuredComponent(message.content)) {
    const structuredContent = message.content; // Type narrowed by type guard
    
    // Check if this is a welcome message component to hide avatar
    const isWelcomeMessage = !isMultiComponentResponse(structuredContent) && 
                           structuredContent.componentType === 'welcomeMessage';
    
    const handleOptionSelect = (option: string) => {
      // Send the selected option as a new message to the backend
      if (onSendMessage) {
        onSendMessage(option);
      }
    };

    const handleCtaClick = (offer: unknown) => {
      // TODO: Add callback for CTA clicks if needed
      console.log('CTA clicked:', offer);
    };

    // Handle multi-component response (array of components)
    if (isMultiComponentResponse(structuredContent)) {
      // Define component types that should be displayed in carousel
      const carouselComponentTypes = ['offersCard', 'offersComparisonCard', 'currentPlanCard', 'currentplancard', 'usageCard', 'optionsSummaryCard', 'selectedPlanCard'];
      
      // Also log what's available in ComponentRegistry
      console.log('Available components in registry:', Object.keys(ComponentRegistry));
      
      // Debug logging
      console.log('Multi-component detected:', {
        componentCount: structuredContent.components.length,
        componentTypes: structuredContent.components.map(c => c.componentType),
        carouselComponentTypes
      });
      
      // Check compatibility for each component
      const compatibilityCheck = structuredContent.components.map(component => ({
        componentType: component.componentType,
        isCompatible: carouselComponentTypes.includes(component.componentType)
      }));
      
      console.log('Component compatibility check:', compatibilityCheck);
      
      // Check if any components in the array should be in carousel (at least one compatible)
      const compatibleComponents = structuredContent.components.filter(component => 
        carouselComponentTypes.includes(component.componentType)
      );
      
      // Calculate total carousel items considering array content
      const totalCarouselItems = compatibleComponents.reduce((total, component) => {
        if (Array.isArray(component.content)) {
          return total + component.content.length;
        }
        return total + 1;
      }, 0);
      
      const hasAnyCompatibleComponents = compatibleComponents.length > 0;
      const hasMoreThanOneCarouselItem = totalCarouselItems > 1;
      
      const shouldUseCarousel = hasMoreThanOneCarouselItem;
      
      console.log('Carousel decision:', {
        shouldUseCarousel,
        totalComponents: structuredContent.components.length,
        compatibleComponentsCount: compatibleComponents.length,
        totalCarouselItems,
        hasAnyCompatibleComponents,
        hasMoreThanOneCarouselItem,
        compatibleComponentTypes: compatibleComponents.map(c => c.componentType),
        allComponentsCompatible: structuredContent.components.every(component => 
          carouselComponentTypes.includes(component.componentType)
        )
      });

      return (
        <div
          className={`message${isUser ? ' message--user' : ''} ${isLatest ? 'slide-up' : ''}`}
          style={{
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            marginLeft: isUser ? 'auto' : undefined,
            marginRight: isUser ? undefined : 'auto',
            display: 'flex'
          }}
        >
          {!isUser ? (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {!isWelcomeMessage && (
                <div className="message__avatar message__avatar--assistant" style={{ marginBottom: '6px', marginLeft: '2px' }}>
                  <img src={moonIcon} alt="Agent" style={{ width: 18, height: 18, borderRadius: '50%', background: 'none', backgroundColor: 'transparent' }} />
                </div>
              )}
              <div className="message__content" style={{ width: '100%' }}>
                {shouldUseCarousel ? (
                  // Render as carousel
                  (() => {
                    console.log('Attempting to render carousel...');
                    const CarouselComponent = ComponentRegistry['carousel'];
                    console.log('CarouselComponent found:', !!CarouselComponent);
                    
                    if (CarouselComponent) {
                      // Auto-detect view mode based on container
                      let detectedViewMode: 'widget' | 'fullscreen' = 'widget';
                      
                      // Try to detect container width
                      if (typeof window !== 'undefined') {
                        const chatContainer = document.querySelector('.chatbot, .chat-interface');
                        if (chatContainer) {
                          const containerWidth = chatContainer.clientWidth;
                          detectedViewMode = containerWidth > 600 ? 'fullscreen' : 'widget';
                        }
                      }
                      
                      // Use the pre-calculated compatible components and expand array content
                      const expandedItems: Array<{ componentType: string; content: unknown }> = [];
                      
                      compatibleComponents.forEach(component => {
                        // Check if content is an array (backend grouped same componentType)
                        if (Array.isArray(component.content)) {
                          // Expand each item in the array as a separate carousel item
                          component.content.forEach(contentItem => {
                            expandedItems.push({
                              componentType: component.componentType,
                              content: contentItem
                            });
                          });
                        } else {
                          // Single content item
                          expandedItems.push({
                            componentType: component.componentType,
                            content: component.content
                          });
                        }
                      });
                      
                      console.log('Expanded carousel items:', {
                        originalItems: compatibleComponents.length,
                        expandedItems: expandedItems.length,
                        expandedItemTypes: expandedItems.map(item => item.componentType)
                      });
                      
                      const carouselProps = {
                        items: expandedItems,
                        handlers: {
                          onOptionSelect: handleOptionSelect,
                          onCtaClick: handleCtaClick,
                          onSendMessage: onSendMessage
                        },
                        viewMode: detectedViewMode
                      };
                      
                      console.log('Carousel props:', carouselProps);
                      return React.createElement(CarouselComponent, carouselProps);
                    } else {
                      console.error('CarouselComponent not found in ComponentRegistry');
                      return <div style={{color: 'red', padding: '10px'}}>Carousel component not available</div>;
                    }
                  })()
                ) : (
                  // Render components individually (original behavior)
                  (() => {
                    console.log('Rendering components individually instead of carousel');
                    return structuredContent.components.map((component, index) => {
                      console.log('Looking for component:', component.componentType);
                      console.log('Available components in registry:', Object.keys(ComponentRegistry));
                      const Component = ComponentRegistry[component.componentType];
                      if (!Component) {
                        console.error(`Component not found: ${component.componentType}. Available:`, Object.keys(ComponentRegistry));
                        return (
                          <div key={index} className="structured-component-error">
                            Unknown component type: {component.componentType}
                          </div>
                        );
                      }
                      
                      const props = getComponentProps(component.componentType, component.content, {
                        onOptionSelect: handleOptionSelect,
                        onCtaClick: handleCtaClick,
                        onSendMessage: onSendMessage
                      }, false); // Not in carousel

                      return React.createElement(Component, { key: index, ...(props as Record<string, unknown>) });
                    });
                  })()
                )}
                
                {/* Render any incompatible components individually when using carousel */}
                {shouldUseCarousel && (() => {
                  const incompatibleComponents = structuredContent.components.filter(component => 
                    !carouselComponentTypes.includes(component.componentType)
                  );
                  
                  if (incompatibleComponents.length > 0) {
                    console.log('Rendering incompatible components individually:', incompatibleComponents.map(c => c.componentType));
                    return incompatibleComponents.map((component, index) => {
                      console.log('Looking for incompatible component:', component.componentType);
                      console.log('Available components in registry:', Object.keys(ComponentRegistry));
                      const Component = ComponentRegistry[component.componentType];
                      if (!Component) {
                        console.error(`Incompatible component not found: ${component.componentType}. Available:`, Object.keys(ComponentRegistry));
                        return (
                          <div key={`incompatible-${index}`} className="structured-component-error">
                            Unknown component type: {component.componentType}
                          </div>
                        );
                      }
                      
                      const props = getComponentProps(component.componentType, component.content, {
                        onOptionSelect: handleOptionSelect,
                        onCtaClick: handleCtaClick,
                        onSendMessage: onSendMessage
                      }, false); // Not in carousel (incompatible components)

                      return React.createElement(Component, { key: `incompatible-${index}`, ...(props as Record<string, unknown>) });
                    });
                  }
                  return null;
                })()}
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    // Handle single structured component
    const renderStructuredComponent = () => {
      // Type guard to ensure we have a single component
      if ('componentType' in structuredContent) {
        const Component = ComponentRegistry[structuredContent.componentType];
        if (!Component) {
          return (
            <div className="structured-component-error">
              Unknown component type: {structuredContent.componentType}
            </div>
          );
        }

        const props = getComponentProps(structuredContent.componentType, structuredContent.content, {
          onOptionSelect: handleOptionSelect,
          onCtaClick: handleCtaClick,
          onSendMessage: onSendMessage
        }, false); // Single component, not in carousel

        return React.createElement(Component, props as Record<string, unknown>);
      }
      
      return (
        <div className="structured-component-error">
          Invalid structured component format
        </div>
      );
    };

    return (
      <div
        className={`message${isUser ? ' message--user' : ''} ${isLatest ? 'slide-up' : ''}`}
        style={{
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          marginLeft: isUser ? 'auto' : undefined,
          marginRight: isUser ? undefined : 'auto',
          display: 'flex'
        }}
      >
        {!isUser ? (
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {!isWelcomeMessage && (
              <div className="message__avatar message__avatar--assistant" style={{ marginBottom: '6px', marginLeft: '2px' }}>
                <img src={moonIcon} alt="Agent" style={{ width: 18, height: 18, borderRadius: '50%', background: 'none', backgroundColor: 'transparent' }} />
              </div>
            )}
            <div className="message__content" style={{ width: '100%' }}>
              <div className="message__structured-content">
                {renderStructuredComponent()}
              </div>
            </div>
          </div>
        ) : (
          <div className="message__content">
            <div className="message__structured-content">
              {renderStructuredComponent()}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback to current implementation for regular text messages
  const shouldRenderAsMarkdown = isAssistant && (typeof message.content === 'string') && (
    /^#{1,6}\s+.+$/m.test(message.content) ||
    /\*\*.+\*\*/.test(message.content) ||
    /\*.+\*/.test(message.content) ||
    /\[.+\]\(.+\)/.test(message.content) ||
    /^[-*+]\s+.+$/m.test(message.content) ||
    /^\d+\.\s+.+$/m.test(message.content) ||
    /^>.+$/m.test(message.content) ||
    /`[^`]+`/.test(message.content) ||
    /^```[\s\S]*?```$/m.test(message.content) ||
    /^\|.+\|$/m.test(message.content)
  );

  return (
    <div
      className={`message${isUser ? ' message--user' : ''} ${isLatest ? 'slide-up' : ''}`}
      style={{
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginLeft: isUser ? 'auto' : undefined,
        marginRight: isUser ? undefined : 'auto',
        display: 'flex'
      }}
    >
      {!isUser ? (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div className="message__avatar message__avatar--assistant" style={{ marginBottom: '6px', marginLeft: '2px' }}>
            <img src={moonIcon} alt="Agent" style={{ width: 18, height: 18, borderRadius: '50%', background: 'none', backgroundColor: 'transparent' }} />
          </div>
          <div className="message__content" style={{ width: '100%' }}>
            <div className="message__text">
              {isAssistant && shouldStream && (!message.retrievalResults || message.retrievalResults.length <= 1) ? (
                shouldRenderAsMarkdown ? (
                  <StreamingText
                    text={message.content}
                    speed={200}
                    onComplete={onStreamComplete}
                    onTextUpdate={onStreamUpdate}
                    renderAs="markdown"
                  />
                ) : (
                  <StreamingText
                    text={message.content}
                    speed={150}
                    onComplete={onStreamComplete}
                    onTextUpdate={onStreamUpdate}
                  />
                )
              ) : shouldRenderAsMarkdown ? (
                <></>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ 
                    __html: (message.content || '')
                      .replace(/\\"Other\\"/g, '"Other"')
                      .replace(/\\\\n/g, '<br />') // Handle double-escaped newlines
                      .replace(/\\n/g, '<br />') // Handle escaped newlines  
                      .replace(/\n/g, '<br />') // Handle actual newlines
                  }}
                />
              )}
            </div>
            {isAssistant && message.retrievalResults && message.retrievalResults.length > 0 && (
              <RetrievalResults
                results={message.retrievalResults}
                maxResults={3}
                shouldStream={shouldStream && message.retrievalResults.length > 1}
                onStreamComplete={onStreamComplete}
                onStreamUpdate={onStreamUpdate}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="message__content">
          <div className="message__text">
            <div style={{ whiteSpace: 'pre-wrap' }}
              dangerouslySetInnerHTML={{ 
                __html: (message.content || '')
                  .replace(/\\"Other\\"/g, '"Other"')
                  .replace(/\\\\n/g, '<br />') // Handle double-escaped newlines
                  .replace(/\\n/g, '<br />') // Handle escaped newlines
                  .replace(/\n/g, '<br />') // Handle actual newlines
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export { Message };
